// ContentFrame Component
// ======================
// A wrapper component that implements the 'white canvas on calm background' principle.
// Provides the visual frame effect with white background, soft rounded corners,
// and subtle shadow or thin border. Feature #277
// Updated: Feature #283 - Added optional DesignLine top accent

import React from 'react';
import DesignLine from './DesignLine';

interface ContentFrameProps {
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Padding size - md uses p-6, lg uses p-8 */
  padding?: 'sm' | 'md' | 'lg' | 'none';
  /** Use border instead of shadow for a lighter visual effect */
  variant?: 'shadow' | 'border';
  /** Maximum width constraint */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | 'full' | 'none';
  /** Center the frame horizontally */
  centered?: boolean;
  /** Feature #283: Add DesignLine accent at top of frame */
  showAccent?: boolean;
}

const ContentFrame: React.FC<ContentFrameProps> = ({
  children,
  className = '',
  padding = 'md',
  variant = 'shadow',
  maxWidth = 'none',
  centered = false,
  showAccent = false,
}) => {
  // Padding styles - generous padding for 'luft' (whitespace)
  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  // Max-width styles
  const maxWidthStyles = {
    none: '',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    full: 'w-full',
  };

  // Variant styles - shadow or border
  const variantStyles = {
    shadow: 'shadow-sm',
    border: 'border border-frame-border dark:border-frame-border-dark',
  };

  return (
    <div
      className={`
        bg-canvas dark:bg-canvas-dark
        rounded-lg
        overflow-hidden
        ${variantStyles[variant]}
        ${maxWidthStyles[maxWidth]}
        ${centered ? 'mx-auto' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Feature #283: Optional DesignLine accent at top */}
      {showAccent && <DesignLine variant="top-accent" height={2} />}
      <div className={paddingStyles[padding]}>
        {children}
      </div>
    </div>
  );
};

export default ContentFrame;
